// Shared helpers for the Employment Details view (applicant + co-applicant) in
// the Loan Summary overview. Field names differ by journey/applicant:
//   applicant:     employmentisEmployed / employmentType / employmentCompanyAddress1
//   co-applicant:  employmentCoApplicant1isEmployed / employmentCoApplicant1EmploymentType / ...companyAddress1
//   partner API:   isEmployed / employmentType / ...
// so matching is done on the name suffix (or label) instead of exact names.

const getName = (field: any) =>
  String(field?.name || field?.key || "").toLowerCase();

const getLabel = (field: any) => String(field?.label || "").trim().toLowerCase();

const toList = (items: any): any[] => {
  if (!items) return [];
  return Array.isArray(items) ? items : Object.values(items);
};

const isYes = (value: any) =>
  value === true || String(value ?? "").trim().toLowerCase() === "yes" ||
  String(value ?? "").trim().toLowerCase() === "true";

const normalize = (value: any) =>
  String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Company Name belongs to Employment Details, not to Company Address
export const isCompanyNameField = (field: any) =>
  normalize(field?.name || field?.key).endsWith("companyname") ||
  ["companyname", "nameofcompany"].includes(normalize(field?.label));

// Company address fields (address lines, landmark, pincode, city, state, country)
// Address parts that appear without a "company" prefix, e.g. co-applicant
// employmentCoApplicant1pincode / employmentCoApplicant1city / employmentCoApplicant1state
const COMPANY_ADDRESS_SUFFIX_REGEX =
  /(address(line)?[123]|landmark|pincode|pin|city|state|country)$/;

export const isCompanyAddressField = (field: any) => {
  if (isCompanyNameField(field)) return false;
  const name = getName(field);
  // ("work" is not matched: it would pull in e.g. employmentCurrentlyWorking)
  return /company|office/.test(name) || COMPANY_ADDRESS_SUFFIX_REGEX.test(normalize(name));
};

// Label inside the Company Address section: drop the "Company" prefix
// ("Company Address1" -> "Address 1", "Company Country" -> "Country")
export const getCompanyAddressLabel = (label: any) => {
  const text = String(label ?? "")
    .replace(/^\s*company\s*/i, "")
    .replace(/([a-zA-Z])(\d+)$/, "$1 $2")
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : String(label ?? "");
};

// Display order inside the Company Address section
const COMPANY_ADDRESS_ORDER: RegExp[] = [
  /address(line)?1$/,
  /address(line)?2$/,
  /address(line)?3$/,
  /landmark$/,
  /pin(code)?$/,
  /city$/,
  /state$/,
  /country$/,
];

const getCompanyAddressRank = (field: any) => {
  const candidates = [normalize(field?.name || field?.key), normalize(field?.label)];
  const rank = COMPANY_ADDRESS_ORDER.findIndex((pattern) =>
    candidates.some((candidate) => candidate && pattern.test(candidate)),
  );
  return rank === -1 ? COMPANY_ADDRESS_ORDER.length : rank;
};

// Address Line 1, 2, 3, Landmark, Pincode, City, State, Country, then anything else
export const sortCompanyAddressFields = <T>(fields: T[] | undefined | null): T[] =>
  [...(fields || [])]
    .map((field, index) => ({ field, index, rank: getCompanyAddressRank(field) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ field }) => field);

// Company Address is shown only when the person is employed and the
// employment type is not Farmer / Others
export const shouldShowCompanyAddress = (items: any) => {
  const list = toList(items);

  const isEmployedField = list.find(
    (item) => getName(item).endsWith("isemployed") || getLabel(item) === "is employed",
  );
  const employmentTypeField = list.find(
    (item) =>
      getName(item).endsWith("employmenttype") || getLabel(item) === "employment type",
  );

  if (!isYes(isEmployedField?.value)) return false;

  const employmentType = String(employmentTypeField?.value ?? "").trim().toLowerCase();
  return !["farmer", "others"].includes(employmentType);
};
